import { useEffect, useRef, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../Components/context/auth-context';
import {
  projectActions,
  readStoredProject,
  removeStoredProject,
} from '../store/project';
import ProjectsAPI from '../services/api/admin/projects-api';
import Logger from '../utils/logger';

/**
 * Rehydrates the globally selected project once after authentication resolves.
 *
 * Reads the per-user localStorage entry, validates the stored project id against
 * the live active-project list, and dispatches selectProject with fresh server
 * data (name/number may have changed since last session) on success, or
 * clearProject + removes the stale entry on any failure (deleted, archived,
 * network error).
 */
export const useProjectRehydration = () => {
  const dispatch = useDispatch();
  const auth = useContext(AuthContext);
  const { token, userId } = auth;

  // Guard so rehydration runs only once per authenticated session.
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!token || !userId || hasRunRef.current) return;
    hasRunRef.current = true;

    const stored = readStoredProject(userId);
    if (!stored || !stored.id) {
      // Nothing stored — leave the no-selection state as-is.
      return;
    }

    let cancelled = false;

    const validate = async () => {
      try {
        const activeProjects = await ProjectsAPI.getActive(token);
        const match = activeProjects.find((p) => p.id === stored.id);

        if (cancelled) return;

        if (match) {
          // Dispatch fresh server data, not the (possibly stale) stored copy.
          dispatch(
            projectActions.selectProject({
              id: match.id,
              number: match.number,
              name: match.name,
              default_trade_id: match.default_trade_id,
            })
          );
        } else {
          Logger.info('Stored project no longer active — clearing', {
            storedProjectId: stored.id,
          });
          removeStoredProject(userId);
          dispatch(projectActions.clearProject());
        }
      } catch (err) {
        if (cancelled) return;
        Logger.error('Failed to validate stored project — clearing', err);
        removeStoredProject(userId);
        dispatch(projectActions.clearProject());
      }
    };

    validate();

    return () => {
      cancelled = true;
    };
  }, [token, userId, dispatch]);
};

export default useProjectRehydration;
